<template>
	<main class="cc-contacts" data-tour="cc-contacts-overview">
		<h1 class="page-title">Contacts</h1>
		<ContactFilters
			:activeContacts="activeInd"
			:filterStatus="filterStatus"
			:selectAllMode="selectAllMode"
			:selectedLeadsCount="selectedLeadsCount"
			@cancelSelectAllMode="handleCancelSelectAllMode"
			@apiCallError="handleApiCallError"
			@resetFilterStatus="resetFilterStatus"
			@updateContacts="refreshContacts"
		/>
		<SelectionStatus
			:leadIdsCount="selectedLeadsCount"
			:selectAllMode="selectAllMode"
			:totalCount="totalCount"
			@updateSelectAllMode="handleSelectAllMode"
		/>
		<nav
			class="contact-type-nav"
			aria-label="Active or Inactive Contact Navigation"
		>
			<RouterLink :to="{ name: 'Active' }">Active</RouterLink>
			<RouterLink :to="{ name: 'Inactive' }">Inactive</RouterLink>
		</nav>
		<FallBack
			v-if="showNoResults"
			data-test="no-results"
			iconStyle="fal"
			icon="exclamation-triangle"
			message="Sorry, there are no results."
		/>
		<FallBack
			v-if="showNoContacts"
			iconStyle="far"
			icon="users"
			data-test="no-contacts"
		>
			<p aria-label="warning or error message">
				You have no contacts.
				<TextButton
					aria-label="Add a contact"
					@onClick="handleAddSingleContact"
				>
					Add a Contact.
				</TextButton>
			</p>
		</FallBack>
		<FallBack
			v-if="showError"
			data-test="show-error"
			iconStyle="fal"
			icon="exclamation-triangle"
			message="We could not retrieve your contacts, please try again later. "
		/>
		<div v-if="showTable">
			<RouterView></RouterView>
			<Pagination
				v-if="showPagination"
				:pageNumber="pageNumber"
				:pageSize="pageSize"
				:totalCount="totalCount"
				@onPageChange="handlePageNumberChange"
				@onPageSizeChange="handlePageSizeChange"
			/>
		</div>
		<FallBack v-if="loading">
			<LoadingIcon />
		</FallBack>

		<OverviewModal
			v-if="showOverviewModal"
			:blinkDisabled="isImpersonated"
			:blinkUnregistered="blinkUnregistered"
			:leadId="leadIdClicked"
			:leadInfo="leadInfo"
			:loggedInUserId="loggedInUserId"
			:modalHistory="modalHistory"
			:modalPreference="modalPreference"
			:modalView="modalView"
			:removeBackCaret="removeModalBackCaret"
			:selectAllMode="selectAllMode"
			:selectedLeadIds="selectedLeadIds"
			:selectedLeadsCount="selectedLeadsCount"
			:sendContact="sendContactToBlink"
			:typeOfNote="'lead'"
			@apiCallError="handleApiCallError"
			@cancelFilterSelection="handleCancelFilterSelection"
			@clearLeadIds="handleClearLeadIds"
			@close="closeModal"
			@confirmFilterSelection="handleConfirmFilterSelection"
			@filterLeadId="handleFilterLeadId"
			@refreshContacts="refreshContacts"
			@refreshCurrentPageContacts="refreshCurrentPageContacts"
			@updateContactList="updateContactList"
		/>
		<MultipleContactsImportModal
			:showModal="showMultipleContactsImportModal"
			@importSuccess="refreshContacts"
			@closeModal="closeMultipleContactsImportModal"
		/>
	</main>
</template>

<script>
import { isEmpty } from 'lodash';
import { mapActions, mapState, mapGetters } from 'vuex';

import OverviewModal from '../modals/OverviewModal.vue';
import FallBack from '@/components/global/FallBack.vue';
import ContactFilters from './ContactFilters.vue';
import SelectionStatus from '@/components/global/SelectionStatus.vue';
import LoadingIcon from '@/components/global/LoadingIcon.vue';
import Pagination from '@/components/global/Pagination.vue';
import TextButton from '@/components/global/TextButton.vue';
import MultipleContactsImportModal from '../modals/MultipleContactsImportModal.vue';
import { scrollToTop } from '../helpers';
import { UPDATE_CONTACTS } from '@/store/modules/constants/contacts.js';

export default {
	name: 'ClientConnectV2Contacts',
	components: {
		Pagination,
		FallBack,
		LoadingIcon,
		TextButton,
		ContactFilters,
		SelectionStatus,
		OverviewModal,
		MultipleContactsImportModal,
	},
	data() {
		return {
			endpointStartPage: 1,
			filterStatus: '',
			pageNumber: 1,
			refreshCurrentPageContactsKey: 0,
		};
	},
	computed: {
		...mapGetters(['isImpersonated', 'noLeadsFound', 'noSearchResultsFound']),
		...mapState({
			// ccContacts
			blinkUnregistered: (state) => state.ccContacts.blink.blinkUnregistered,
			contacts: (state) => state.ccContacts.leads.items,
			contactIndex: (state) => state.ccContacts.leads.index,
			contactUpdateType: (state) => state.ccContacts.leads.contactUpdateType,
			leadIdClicked: (state) => state.ccContacts.leads.leadIdClicked,
			leadInfo: (state) => state.ccContacts.blink.leadInfo,
			leadsError: (state) => state.ccContacts.leads.error,
			loading: (state) => state.ccContacts.views.loading,
			modalHistory: (state) => state.ccContacts.views.modalHistory,
			modalView: (state) => state.ccContacts.views.modalView,
			totalCount: (state) => state.ccContacts.leads.totalCount,
			modalPreference: (state) => state.ccContacts.views.modalPreference,
			pageSize: (state) => state.ccContacts.views.pageSize,
			removeModalBackCaret: (state) =>
				state.ccContacts.views.removeModalBackCaret,
			selectAllMode: (state) => state.ccContacts.views.selectAllMode,
			selectedLeadIds: (state) => state.ccContacts.leads.selectedLeadIds,
			sendContactToBlink: (state) => state.ccContacts.blink.sendContactToBlink,
			showMultipleContactsImportModal: (state) =>
				state.ccContacts.views.showMultipleContactsImportModal,
			showOverviewModal: (state) => state.ccContacts.views.showOverviewModal,
			sort: (state) => state.ccContacts.views.sort,
			// for owner filter search
			selectedContactIdsUpdated: (state) =>
				state.clientConnectV2.selectedContactIdsUpdated,
			// profileSwitcher
			loggedInUserId: (state) =>
				state.profileSwitcher.mainProfile.rolodexContactId,
		}),
		activeInd() {
			return this.$route.name !== 'Inactive';
		},
		currentPageIndexStart() {
			return (this.pageNumber - 1) * this.pageSize;
		},
		currentPageIndexEnd() {
			return this.pageNumber * this.pageSize;
		},
		currentPageContacts() {
			/**
			 * unlike what synk suggests, this does have an effect
			 * This value forces an update to currentPageContacts
			 * when the value is changed in the front end.
			 */
			// deepcode ignore NoEffectExpression: see note above
			this.refreshCurrentPageContactsKey;
			return this.contacts.slice(
				this.currentPageIndexStart,
				this.currentPageIndexEnd,
			);
		},
		moreContactsAvaliable() {
			return this.totalCount > this.contacts.length;
		},
		pagesLoaded() {
			return Math.ceil(this.contacts.length / this.pageSize) || 1;
		},
		showPagination() {
			return !this.loading && this.totalCount > this.pageSize;
		},
		showNoResults() {
			if (this.loading) return false;
			
			return (
				(!this.noLeadsFound && !this.leadsError && this.noSearchResultsFound) ||
				(this.noLeadsFound && this.$route.name === 'Inactive')
			);
		},
		showNoContacts() {
			return (
				!this.loading &&
				!this.leadsError &&
				this.noLeadsFound &&
				this.$route.name !== 'Inactive'
			);
		},
		showError() {
			return !this.loading && this.leadsError;
		},
		showTable() {
			return (
				!this.loading &&
				!this.noLeadsFound &&
				!this.noSearchResultsFound &&
				!this.leadsError
			);
		},
		selectedLeadsCount() {
			return this.selectedLeadIds.length;
		},
	},
	methods: {
		...mapActions([
			'clearLeads',
			'getLeads',
			'getLoanFilterOptions',
			'handleAddSingleContact',
			'setCCContactsLoading',
			'setCCContactsPageSize',
			'setContactUpdateType',
			'setCurrentPageContacts',
			'setLeadIdClicked',
			'setLeadsError',
			'setSelectAllMode',
			'setSelectedLeadIds',
			'setShowMultipleContactsImportModal',
			'setShowOverviewModal',
			'setSort',
			'showApiErrorModal',
		]),
		/**
		 * when we use a function in the template that is imported from outside
		 * the component it needs to be included in the methods object
		 */
		// deepcode ignore VueIncorrectMethodType: see note above
		isEmpty,
		handlePageNumberChange(page) {
			this.pageNumber = page;
		},
		handlePageSizeChange(newPageSize) {
			if (this.pageSize !== newPageSize) {
				this.pageNumber = Math.floor(
					(this.pageNumber * this.pageSize) / newPageSize -
						(this.pageSize / newPageSize - 1),
				);
				this.setCCContactsPageSize(newPageSize);
				scrollToTop();
			}
		},
		handleApiCallError(source) {
			/**
			 * this will handle other errors in the future, this switch allows
			 * other messages to be sent
			 */
			let errorMessage;
			switch (source) {
				case 'saveContact':
					errorMessage = 'There was a problem saving your contact.';
					break;
				case 'exportContact':
					errorMessage = 'We are unable to export contacts at this time.';
					break;
				default:
					errorMessage = 'There was a problem...';
			}

			this.closeModal();
			this.showApiErrorModal({
				message: errorMessage,
				buttonLabel: 'Return to Client Connect Contacts',
				buttonAction: 'close',
			});
		},
		closeMultipleContactsImportModal() {
			this.setShowMultipleContactsImportModal(false);
		},
		handleFilterLeadId() {
			this.setSelectedLeadIds(
				this.selectedLeadIds.filter((id) => id !== this.leadIdClicked),
			);
			this.setLeadIdClicked(null);
		},
		handleClearLeadIds() {
			this.setSelectedLeadIds([]);
			this.setSelectAllMode(false);
			this.setLeadIdClicked(null);
		},
		closeModal() {
			this.setShowOverviewModal(false);
			this.setLeadIdClicked(null);
		},
		refreshContacts() {
			this.setContactUpdateType(UPDATE_CONTACTS.REFRESH_CONTACTS);
			this.setCCContactsLoading(true);
			this.pageNumber = 1;
			this.endpointStartPage = 1;
			this.getContacts();
		},
		replaceContacts() {
			this.setContactUpdateType(UPDATE_CONTACTS.REPLACE_CONTACTS);
			this.getContacts();
		},
		appendContacts() {
			this.setContactUpdateType(UPDATE_CONTACTS.APPEND_CONTACTS);
			this.setCCContactsLoading(true);
			this.getContacts();
		},
		updateContactList({ id }) {
			if (id) {
				this.setLeadIdClicked(id);
			}
			this.replaceContacts();
		},
		refreshCurrentPageContacts() {
			this.refreshCurrentPageContactsKey++;
		},
		pushRoute(name) {
			if (this.$route.name !== name) {
				this.$router.push({ name: name });
			}
		},
		getContacts() {
			this.getLeads({
				activeInd: this.activeInd,
				pageNumber: this.endpointStartPage,
				pageSize: this.pageSize,
			});
		},
		getStartPage() {
			/**
			 * endpointStartPage is the page where the API call should start,
			 * the endpoint returns 5 pages of contacts at a time,
			 * this formula returns the correct starting page for
			 * any given pageNumber. 3 returns 1, 8 returns 6, 14 returns 11
			 */
			let startPage = 5 * Math.floor((this.pageNumber - 1) / 5) + 1;

			return startPage;
		},
		handleSelectAllMode(selectAllMode) {
			this.setSelectAllMode(selectAllMode);
		},
		handleCancelSelectAllMode() {
			this.setSelectAllMode(false);
		},
		handleConfirmFilterSelection() {
			this.filterStatus = 'confirmed';
		},
		handleCancelFilterSelection() {
			this.filterStatus = 'canceled';
		},
		resetFilterStatus() {
			this.filterStatus = '';
		},
	},
	watch: {
		async currentPageContacts(newPageContacts) {
			this.setCurrentPageContacts(newPageContacts);
			if (this.moreContactsAvaliable && !isEmpty(this.contacts)) {
				if (isEmpty(newPageContacts)) {
					this.endpointStartPage = this.getStartPage();
					this.appendContacts();
				} else if (newPageContacts.length !== this.pageSize) {
					this.endpointStartPage = this.getStartPage();
					this.replaceContacts();
				}
			} else if (isEmpty(newPageContacts)) {
					this.pageNumber = this.pagesLoaded;
					this.endpointStartPage = this.getStartPage();
			}
		},
		selectAllMode() {
			this.setSelectedLeadIds([]);
		},
		selectedLeadsCount(newCount) {
			if (this.totalCount === 0) return;
			if (newCount === this.totalCount) {
				this.setSelectAllMode(!this.selectAllMode);
			}
		},
		sort(newValue) {
			if (newValue === true) {
				this.refreshContacts();
				this.setSort(false);
			}
		},
		$route(newRoute) {
			if (newRoute.name === 'CCV2-Contacts') {
				this.pushRoute('Active');
			}
			this.handleClearLeadIds();
			this.refreshContacts();
		},
	},
	mounted() {
		if (this.$route.name !== 'Inactive') {
			this.pushRoute('Active');
		}
		if (this.$route.name === 'Active') {
			this.refreshContacts();
		}
	},
};
</script>

<style scoped lang="scss">
.cc-contacts {
	@include rem('margin-bottom', 30px);
}

.contact-type-nav {
	display: flex;
	align-items: center;
	justify-content: flex-start;

	a {
		@include rem('padding', 16px $base-padding);
		text-decoration: none;
		position: relative;
		color: $text-base-color;

		&:hover {
			color: $brand-blue;
		}
		&:focus,
		&.router-link-active {
			color: $brand-blue;

			&:after {
				content: '';
				position: absolute;
				width: 100%;
				height: 2px;
				left: 0;
				bottom: 0;
				background: $brand-blue;
			}
		}
	}
}
</style>
